/**
 * Turning a series into an SVG path, and the gap rule that is the whole point
 * of this module.
 *
 * **A gap breaks the path.** A 3-hourly GFS is not a gap — it is what that
 * model publishes past f120 — and a missing HRRR step is. Drawing straight
 * through a hole invents six hours of forecast, and doing it with a *smooth*
 * curve invents them prettily. So the break is measured against the series'
 * own nominal spacing rather than against a constant: a series decides what
 * "a step" means for it, and anything much longer than that is a hole.
 *
 * Straight segments, never a spline. A line between two real points is a
 * visual connection; a curve through them is a claim about the values in
 * between, and no model forecast those.
 */

export interface Point {
  t: number
  /** null is a hole: a stated absence, or a value the model did not give. */
  v: number | null
}

/** How much longer than the nominal spacing a gap has to be to break the line. */
export const GAP_FACTOR = 1.5

/**
 * The series' own step, as the median of the gaps between consecutive points.
 *
 * The median rather than the minimum or the mean: GFS is hourly for five days
 * and 3-hourly for eleven, so its minimum says "one hour" and would break the
 * line at every one of the 3-hourly steps, while its mean is a number that is
 * not a spacing of anything.
 */
export function nominalSpacing(points: readonly Point[]): number {
  if (points.length < 2) return Infinity
  const gaps: number[] = []
  for (let i = 1; i < points.length; i++) gaps.push(points[i].t - points[i - 1].t)
  gaps.sort((a, b) => a - b)
  return gaps[Math.floor(gaps.length / 2)] || Infinity
}

/**
 * `M…L…` with a new subpath at every gap and at every hole.
 *
 * `spacing` overrides the measured nominal step, for a caller that knows the
 * model's cadence from the catalog and would rather not infer it.
 */
export function line(
  points: readonly Point[],
  sx: (t: number) => number,
  sy: (v: number) => number,
  spacing?: number,
): string {
  const step = spacing ?? nominalSpacing(points)
  const limit = step * GAP_FACTOR
  let out = ''
  let pen = false
  let previous: Point | null = null

  for (const point of points) {
    if (point.v === null || !Number.isFinite(point.v)) {
      // A hole is a hole whether it is a missing step or a stated null, and
      // both break the line: the alternative is a segment across a value
      // nobody forecast.
      pen = false
      previous = point
      continue
    }
    const jumped = previous !== null && point.t - previous.t > limit
    const command = !pen || jumped ? 'M' : 'L'
    out += `${command}${round(sx(point.t))},${round(sy(point.v))}`
    pen = true
    previous = point
  }
  return out
}

/**
 * The segments a series would draw, as index ranges. What `line` does, said in
 * a shape a test can assert without parsing a path string.
 */
export function segments(points: readonly Point[], spacing?: number): [number, number][] {
  const step = spacing ?? nominalSpacing(points)
  const limit = step * GAP_FACTOR
  const out: [number, number][] = []
  let start: number | null = null
  let previous: Point | null = null

  points.forEach((point, index) => {
    const usable = point.v !== null && Number.isFinite(point.v)
    const jumped = previous !== null && point.t - previous.t > limit
    if (!usable || jumped) {
      if (start !== null && !usable) {
        out.push([start, index - 1])
        start = null
      } else if (start !== null && jumped) {
        out.push([start, index - 1])
        start = index
      }
    }
    if (usable && start === null) start = index
    previous = point
  })
  if (start !== null) out.push([start, points.length - 1])
  return out.filter(([a, b]) => b >= a)
}

/** One decimal is a tenth of a pixel; more is bytes nobody can see. */
function round(value: number): number {
  return Math.round(value * 10) / 10
}

/**
 * An accumulation, drawn as a step: a horizontal segment across the span the
 * value actually covers, and a vertical where it changes.
 *
 * The plan called for bars, and for one series that is the same picture. It is
 * not the same picture for five: an hourly rainfall field on HRRR, RAP, NAM,
 * NBM and GFS is five bar series at the same instants across 276 px, which is
 * a grouped bar chart with four-pixel bars and no room for the 2 px gaps that
 * make grouped bars readable. A step line is the bar's *outline* — it makes
 * exactly the same claim, that this value covers this span rather than this
 * instant — and five of them overlay legibly where five bars do not.
 *
 * `windowMs` is the series' own span. A point with no span is drawn as a
 * one-pixel-wide tick rather than silently as an instant: a value that covers
 * nothing is a bug upstream, and a line through it would hide it.
 */
export function stepLine(
  points: readonly Point[],
  sx: (t: number) => number,
  sy: (v: number) => number,
  windowMs: number,
): string {
  let out = ''
  let previousEnd: number | null = null
  for (const point of points) {
    if (point.v === null || !Number.isFinite(point.v)) {
      previousEnd = null
      continue
    }
    const start = point.t - Math.max(windowMs, 1)
    const y = round(sy(point.v))
    // Contiguous buckets join into one outline; a gap starts a new one, the
    // same rule `line` follows and for the same reason.
    const command = previousEnd !== null && Math.abs(previousEnd - start) < 1 ? 'L' : 'M'
    out += `${command}${round(sx(start))},${y}L${round(sx(point.t))},${y}`
    previousEnd = point.t
  }
  return out
}
